import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {useCallback, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import DefaultTextField, {
  IDefaultTextFieldProps,
  defaultTextSchema,
} from '../../../../components/utlis/MUI/DefaultTextField';
import IconUploadFile from '../../../../asset/icons/icon_uploadsong.svg';
import {DialogClose} from '../../../../components/dialogs/DialogFrame/DialogClose';
import MusicPreviewBtn from '../../../../components/music/MusicPreviewBtn';
import AudioDurationValidation, {
  getAudioDurationFromFile,
} from '../../../../components/utlis/Audio/GetAudioDuration';
import ValidationMessage from '../../../../components/utlis/ValidationMessage';
import Toast from '../../../../package/Toaster';
import {alertConfig} from '../../../../config/messageConfig';
import {companyIdSelector} from '../../../../store/Slices/companySlice';
import {apiPost} from '../../../../axiosConfig';

const defaultText = {
  value: '',
  error: '',
};

let longSongSessionCount = 0;
let longSongCountInitialized = false;

const UploadSongDialog = ({
  handleSave,
  close,
  buttonText = 'Save',
  isLoading = false,
  stationName,
  libraryName,
}: {
  handleSave: (artist: string, track_name: string, file: File | null) => void;
  close: () => void;
  buttonText?: string;
  isLoading?: boolean;
  stationName?: string;
  libraryName?: string;
}) => {
  const companyId = useSelector(companyIdSelector);
  const [trackName, setTrackName] =
    useState<IDefaultTextFieldProps>(defaultText);
  const [artist, setArtist] = useState<IDefaultTextFieldProps>(defaultText);
  const [file, setFile] = useState<File | null>(null);
  const [fileDuration, setFileDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const validateAndSetFile = useCallback(async (file: File) => {
    if (file.type !== 'audio/mpeg') {
      Toast(alertConfig.mp3_format_error, 'warning');
      return;
    }

    const duration = await getAudioDurationFromFile(file);
    if (duration > 90) {
      if (longSongSessionCount >= 5) {
        Toast(alertConfig.long_song_upload_limit, 'error');
        return;
      }
      longSongSessionCount += 1;
    }

    setFile(file);
    setFileDuration(duration);
  }, []);

  const handleOnChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target?.files && e.target?.files.length) {
        const file = e.target.files[0] as File;
        await validateAndSetFile(file);
      }
    },
    [validateAndSetFile],
  );

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length) {
        const file = e.dataTransfer.files[0] as File;
        await validateAndSetFile(file);
      }
    },
    [validateAndSetFile],
  );

  const isValidDuration = useMemo(() => {
    return fileDuration / 60 <= 5;
  }, [fileDuration]);

  const isValidFileSize = useMemo(() => {
    if (!file) return false;
    const fileSizeInMB = file.size / (1024 * 1024);
    return fileSizeInMB <= 10;
  }, [file]);

  const isSave = useMemo(() => {
    return (
      trackName.value !== '' &&
      artist.value !== '' &&
      trackName.error === '' &&
      artist.error === '' &&
      file &&
      isValidDuration &&
      isValidFileSize
    );
  }, [
    artist.error,
    artist.value,
    file,
    isValidDuration,
    isValidFileSize,
    trackName.error,
    trackName.value,
  ]);

  const handleSaveWithValidation = useCallback(() => {
    if (!file || !companyId) return;

    setIsChecking(true);
    const formData = new FormData();
    formData.append('file', file);

    apiPost(
      `/v1/company/${companyId}/custom-library/check-upload-limit`,
      formData,
    )
      .then(response => {
        if (response.data.data.can_upload) {
          if (!longSongCountInitialized) {
            longSongSessionCount = response.data.data.existing_count + 1;
            longSongCountInitialized = true;
          }
          handleSave(artist.value, trackName.value, file);
        } else {
          Toast(response.data.data.message, 'error');
        }
      })
      .catch(error => {
        Toast(
          error?.response?.data?.data.message ||
            'Unable to validate song duration',
          'error',
        );
      })
      .finally(() => {
        setIsChecking(false);
      });
  }, [file, companyId, artist.value, trackName.value, handleSave]);

  return (
    <Dialog
      fullWidth
      maxWidth='xs'
      open
      onClose={close}
      aria-labelledby='dialog-title'
      aria-describedby='dialog-description'>
      <Box
        className={`blue-variant17 ${isDragging ? 'dragging' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}>
        <DialogTitle>
          <Typography>Upload a Song</Typography>
          <DialogClose onClose={close} />
        </DialogTitle>
        <DialogContent className='px-6 pt-2'>
          {(stationName || libraryName) && (
            <Stack spacing={1} mb={2}>
              {stationName && (
                <Typography variant='body2'>
                  <strong>Station:</strong> {stationName}
                </Typography>
              )}
              {libraryName && (
                <Typography variant='body2'>
                  <strong>Single Play Library:</strong> {libraryName}
                </Typography>
              )}
            </Stack>
          )}
          <AudioDurationValidation
            durationInSec={fileDuration}
            maxDurationInMin={5}
          />
          <Button
            className='h-120 my-3 border-none flex-column items-center'
            component='label'
            variant='contained'
            disableElevation
            fullWidth
            sx={{
              backgroundColor: `var(--blue_variant41)`,
              '&:hover': {backgroundColor: `var(--blue_variant41) !important`},
            }}>
            <Box className='max-w-220 w-full flex justify-between'>
              <img src={IconUploadFile} alt='' />
              <Typography align='center' className='text-white-50'>
                Drag and drop <br />
                Or
                <br />
                <span
                  style={{
                    color: `var(--blue-variant1)`,
                    textDecoration: 'underline',
                  }}>
                  Upload File
                </span>
              </Typography>
              <input
                accept='audio/*'
                type='file'
                id='select-voice'
                style={{
                  display: 'none',
                }}
                value=''
                onChange={handleOnChange}
              />
            </Box>
          </Button>

          {file && (
            <>
              {isValidDuration && isValidFileSize ? (
                <Stack direction='row' spacing={1}>
                  <MusicPreviewBtn
                    id={file.name}
                    url={`${URL.createObjectURL(file)}`}
                    track_name={file.name}
                    track_artist={file.name}
                  />
                  <Typography className='text-center mb-4'>
                    {file?.name}
                  </Typography>
                </Stack>
              ) : null}
              <Stack spacing={1}>
                <ValidationMessage
                  isValid={isValidDuration}
                  message='Maximum play time: 5 mins'
                />
                <ValidationMessage
                  isValid={isValidFileSize}
                  message='Maximum file size: 10 MB'
                />
              </Stack>
            </>
          )}

          <DefaultTextField
            state={artist}
            setState={setArtist}
            schema={defaultTextSchema}
            attr={{id: 'track_artist', label: 'Enter Artist Name'}}
          />
          <DefaultTextField
            state={trackName}
            setState={setTrackName}
            schema={defaultTextSchema}
            attr={{id: 'track_name', label: 'Enter Song Name'}}
          />
        </DialogContent>
        <DialogActions
          className='mt-4 mb-8'
          sx={{justifyContent: 'center !important'}}>
          <Button
            variant='contained'
            className='min-w-150'
            disabled={!isSave || isLoading || isChecking}
            onClick={handleSaveWithValidation}>
            {isChecking
              ? 'Validating...'
              : isLoading
              ? 'Uploading...'
              : buttonText}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default UploadSongDialog;
